import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Gift, Megaphone, Bell, Swords, Gamepad2 } from "lucide-react";
import useFetch from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import CommunityCard from "@/components/CommunityCard";
import DiscordHealthCard from "@/components/DiscordHealthCard";
import { PageHeader, Footer, SectionCard } from "@/components/common/Page";
import StatusBadge from "@/components/common/StatusBadge";
import Loader from "@/components/common/Loader";
import { timeAgo } from "@/lib/communities";
import { Button } from "@/components/ui/button";

export default function Dashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { data: communities } = useFetch("/communities");
    const { data: servers } = useFetch("/servers");
    const { data: events } = useFetch("/events");
    const { data: anns } = useFetch("/announcements");
    const { data: discord } = useFetch("/discord/status");
    const { data: notifs } = useFetch("/notifications");

    if (!communities || !servers) return <Loader />;

    const serverFor = (k) => servers.find((s) => s.community_key === k);
    const eventFor = (k) => (events || []).find((e) => e.community_key === k);

    const openDiscord = (c) => toast.info(`Discord shortcuts for ${c.name}`, { description: "Configure invite links in Admin → Discord." });

    return (
        <div data-testid="player-dashboard">
            <PageHeader
                title={`Welcome back, ${user?.username || "survivor"}`}
                subtitle="Your Outpost Network command center. Connect. Build. Raid. Survive."
                action={
                    <StatusBadge
                        status={user?.discord ? "connected" : "disconnected"}
                        label={user?.discord ? "DISCORD LINKED" : "DISCORD NOT LINKED"}
                        testId="dash-discord-status"
                    />
                }
            />

            {/* quick widgets */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Widget icon={Gamepad2} label="Communities" value={communities.length} accent="#FF6B00" />
                <Widget icon={Swords} label="Players Online" value={communities.reduce((a, c) => a + (c.players_online || 0), 0)} accent="#00FF66" />
                <Widget icon={Megaphone} label="Announcements" value={(anns || []).length} accent="#F59E0B" />
                <Widget icon={Bell} label="Notifications" value={(notifs || []).filter((n) => !n.read).length} accent="#EF4444" />
            </div>

            {/* community cards */}
            <h2 className="font-heading text-2xl font-bold uppercase tracking-wide mb-4">Your Communities</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
                {communities.map((c) => (
                    <CommunityCard key={c.key} community={c} server={serverFor(c.key)} event={eventFor(c.key)} onOpenDiscord={openDiscord} />
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
                <div className="lg:col-span-2 space-y-5">
                    <DiscordHealthCard status={discord} />
                    <SectionCard title="Recent Announcements" testId="dash-announcements">
                        <div className="space-y-3">
                            {(anns || []).slice(0, 4).map((a) => (
                                <div key={a.id} className="flex items-start gap-3 rounded-md border border-border bg-background/40 p-3">
                                    <Megaphone size={16} className="text-hazard mt-0.5" />
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-heading font-semibold uppercase text-foreground truncate">{a.title}</span>
                                            {a.priority === "critical" && <StatusBadge status="failed" label="CRITICAL" />}
                                        </div>
                                        <p className="text-sm text-muted-foreground truncate">{a.message}</p>
                                        <span className="label-mono text-muted-foreground">{timeAgo(a.created_at)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </SectionCard>
                </div>

                <div className="space-y-5">
                    <SectionCard title="Daily Rewards" testId="dash-daily-reward">
                        <div className="text-center py-3">
                            <Gift size={34} className="mx-auto text-radioactive mb-2" />
                            <div className="font-heading text-2xl font-bold text-radioactive text-glow-green">+500 SCRAP</div>
                            <p className="text-xs text-muted-foreground mt-1">Daily login bonus available</p>
                            <Button data-testid="claim-daily-reward" className="mt-3 w-full font-heading uppercase bg-radioactive/90 text-black hover:bg-radioactive"
                                onClick={() => toast.success("Daily reward claimed!", { description: "+500 Scrap added to your wallet." })}>
                                Claim Reward
                            </Button>
                        </div>
                    </SectionCard>

                    <SectionCard title="Notifications" testId="dash-notifications"
                        action={<button className="label-mono text-hazard" onClick={() => navigate("/player/notifications")}>VIEW ALL</button>}>
                        <div className="space-y-2">
                            {(notifs || []).slice(0, 4).map((n) => (
                                <div key={n.id} className="flex items-center gap-2 rounded-md border border-border bg-background/40 p-2.5">
                                    <span className={`h-2 w-2 rounded-full ${n.read ? "bg-muted" : "bg-hazard"}`} />
                                    <div className="min-w-0">
                                        <div className="text-sm text-foreground truncate">{n.title}</div>
                                        <div className="label-mono text-muted-foreground">{timeAgo(n.created_at)}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </SectionCard>
                </div>
            </div>
            <Footer />
        </div>
    );
}

function Widget({ icon: Icon, label, value, accent }) {
    return (
        <div className="tactical-card rounded-md p-4 flex items-center gap-3 animate-fade-up">
            <div className="rounded-md p-2.5" style={{ background: `${accent}18` }}>
                <Icon size={20} style={{ color: accent }} />
            </div>
            <div>
                <div className="font-heading text-2xl font-bold tabular-nums text-foreground">{value}</div>
                <div className="label-mono text-muted-foreground">{label}</div>
            </div>
        </div>
    );
}
